import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ChartLineUp, Plus, Trash, FilePdf, MagnifyingGlass, Check, Users
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropostaInvestimento() {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Dialog: Adicionar Cliente
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);

  // Dialog: Adicionar Painéis/Expositores do catálogo PPM
  const [showPanelDialog, setShowPanelDialog] = useState(false);
  const [panelDialogClientId, setPanelDialogClientId] = useState(null);
  const [panelSearch, setPanelSearch] = useState("");
  const [panelTypeFilter, setPanelTypeFilter] = useState("");
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  useEffect(() => {
    fetchProposal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`${API}/investment-proposals/${proposalId}`);
      setProposal(response.data);
      setNameDraft(response.data.name);
    } catch (error) {
      console.error("Error fetching proposal:", error);
      toast.error("Erro ao carregar proposta");
    } finally {
      setLoading(false);
    }
  };

  // Persiste sempre a proposta inteira (nome + clientes/painéis) e substitui o estado local
  // pela resposta já enriquecida (nomes/moradas/preços resolvidos) devolvida pelo backend.
  const persist = async (clientsForPayload, nameForPayload) => {
    const payload = {
      name: nameForPayload ?? proposal.name,
      clients: clientsForPayload.map(c => ({
        client_id: c.client_id,
        panels: c.panels.map(p => ({ product_id: p.product_id, quantity: p.quantity }))
      }))
    };
    const response = await axios.put(`${API}/investment-proposals/${proposalId}`, payload);
    setProposal(response.data);
    return response.data;
  };

  const handleSaveName = async () => {
    if (!nameDraft.trim() || nameDraft === proposal.name) return;
    setSavingName(true);
    try {
      await persist(proposal.clients, nameDraft.trim());
    } catch (error) {
      console.error("Error saving name:", error);
      toast.error("Erro ao atualizar nome");
    } finally {
      setSavingName(false);
    }
  };

  // ---- Clientes ----
  const handleClientSearchChange = async (value) => {
    setClientSearch(value);
    if (value.trim().length < 2) { setClientResults([]); return; }
    try {
      const response = await axios.get(`${API}/clients?search=${encodeURIComponent(value)}`);
      setClientResults(response.data.filter(c => !proposal.clients.some(pc => pc.client_id === c.id)).slice(0, 8));
    } catch (error) {
      console.error("Error searching clients:", error);
    }
  };

  const handleAddClient = async (clientDoc) => {
    try {
      const newClients = [...proposal.clients, { client_id: clientDoc.id, panels: [] }];
      await persist(newClients);
      setShowClientDialog(false);
      setClientSearch("");
      setClientResults([]);
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Erro ao adicionar cliente");
    }
  };

  const handleRemoveClient = async (clientId) => {
    if (!window.confirm("Remover este cliente da proposta?")) return;
    try {
      const newClients = proposal.clients.filter(c => c.client_id !== clientId);
      await persist(newClients);
      toast.success("Cliente removido");
    } catch (error) {
      console.error("Error removing client:", error);
      toast.error("Erro ao remover cliente");
    }
  };

  // ---- Painéis/Expositores ----
  const openPanelDialog = (clientId) => {
    setPanelDialogClientId(clientId);
    setSelectedProductIds([]);
    setPanelSearch("");
    setPanelTypeFilter("");
    fetchCatalog("", "");
    setShowPanelDialog(true);
  };

  const fetchCatalog = async (search, productType) => {
    try {
      const params = {};
      if (search) params.search = search;
      if (productType) params.product_type = productType;
      const response = await axios.get(`${API}/products`, { params });
      setCatalogProducts(response.data);
    } catch (error) {
      console.error("Error fetching catalog:", error);
    }
  };

  const handlePanelSearchChange = (e) => {
    const value = e.target.value;
    setPanelSearch(value);
    fetchCatalog(value, panelTypeFilter);
  };

  const handlePanelTypeFilterChange = (type) => {
    setPanelTypeFilter(type);
    fetchCatalog(panelSearch, type);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  const handleConfirmAddPanels = async () => {
    if (selectedProductIds.length === 0) return;
    try {
      const targetClient = proposal.clients.find(c => c.client_id === panelDialogClientId);
      const updatedPanels = targetClient.panels.map(p => ({ ...p }));
      selectedProductIds.forEach(productId => {
        const existing = updatedPanels.find(p => p.product_id === productId);
        if (existing) {
          existing.quantity += 1;
        } else {
          updatedPanels.push({ product_id: productId, quantity: 1 });
        }
      });
      const newClients = proposal.clients.map(c => c.client_id === panelDialogClientId ? { ...c, panels: updatedPanels } : c);
      await persist(newClients);
      setShowPanelDialog(false);
      toast.success("Painéis adicionados");
    } catch (error) {
      console.error("Error adding panels:", error);
      toast.error("Erro ao adicionar painéis");
    }
  };

  const handleRemovePanel = async (clientId, productId) => {
    try {
      const newClients = proposal.clients.map(c => c.client_id === clientId
        ? { ...c, panels: c.panels.filter(p => p.product_id !== productId) }
        : c);
      await persist(newClients);
    } catch (error) {
      console.error("Error removing panel:", error);
      toast.error("Erro ao remover painel");
    }
  };

  // Atualiza visualmente de imediato; a quantidade só é persistida ao perder o foco
  // (handleQuantityBlur) para não disparar um pedido a cada tecla premida.
  const handleQuantityChange = (clientId, productId, newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity) || 1);
    setProposal(prev => ({
      ...prev,
      clients: prev.clients.map(c => c.client_id === clientId
        ? { ...c, panels: c.panels.map(p => p.product_id === productId ? { ...p, quantity: qty } : p) }
        : c)
    }));
  };

  const handleQuantityBlur = async () => {
    try {
      await persist(proposal.clients);
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast.error("Erro ao atualizar quantidade");
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/investment-proposals/${proposalId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Proposta_Investimento_${(proposal.name || 'Proposta').replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erro ao gerar PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (<div className="flex items-center justify-center min-h-[400px]"><LoadingSpinner size={40} /></div>);
  }
  if (!proposal) {
    return (<div className="text-center py-16 text-stone-500">Proposta não encontrada</div>);
  }

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="proposta-investimento-page">
      <PageHeader
        icon={ChartLineUp}
        title="Proposta de Investimento"
        subtitle="Selecione painéis/expositores do catálogo PPM para cada cliente"
        onBack={() => navigate('/projetos')}
        actions={
          <Button onClick={handleDownloadPdf} disabled={downloadingPdf} className="bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="btn-download-proposal-pdf">
            <FilePdf size={18} />
            {downloadingPdf ? "A gerar..." : "Descarregar PDF"}
          </Button>
        }
      />

      <div className="gresart-card">
        <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Nome da Proposta</label>
        <div className="flex items-center gap-2 mt-1">
          <Input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={handleSaveName}
            data-testid="input-proposal-name"
          />
          {savingName && <LoadingSpinner size={18} />}
        </div>
      </div>

      {/* Clientes da proposta */}
      <div className="space-y-4">
        {proposal.clients.map((clientData) => (
          <div key={clientData.client_id} className="gresart-card" data-testid={`proposal-client-${clientData.client_id}`}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                {clientData.client_logo_url ? (
                  <img src={clientData.client_logo_url} alt={clientData.client_name} className="w-12 h-12 rounded-lg object-contain bg-stone-100" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center">
                    <Users size={22} className="text-stone-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-stone-900">{clientData.client_name}</h3>
                  {(clientData.client_address || clientData.client_city) && (
                    <p className="text-xs text-stone-500">{[clientData.client_address, clientData.client_city].filter(Boolean).join(' - ')}</p>
                  )}
                </div>
              </div>
              <button onClick={() => handleRemoveClient(clientData.client_id)} className="text-stone-300 hover:text-red-500 transition-colors" data-testid={`remove-client-${clientData.client_id}`}>
                <Trash size={18} />
              </button>
            </div>

            {clientData.panels.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                {clientData.panels.map((panel) => (
                  <div key={panel.product_id} className="relative border border-stone-200 rounded-lg overflow-hidden" data-testid={`proposal-panel-${clientData.client_id}-${panel.product_id}`}>
                    <button
                      onClick={() => handleRemovePanel(clientData.client_id, panel.product_id)}
                      className="absolute top-1.5 right-1.5 z-10 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center text-stone-400 hover:text-red-500 shadow-sm"
                      data-testid={`remove-panel-${clientData.client_id}-${panel.product_id}`}
                    >
                      <Trash size={12} />
                    </button>
                    <div className="bg-stone-50 p-2 h-24 flex items-center justify-center">
                      {panel.image_url ? (
                        <img src={panel.image_url} alt={panel.code} className="max-h-full max-w-full object-contain" />
                      ) : (
                        <span className="text-xs text-stone-400">Sem imagem</span>
                      )}
                    </div>
                    <div className="p-2 space-y-1">
                      <p className="text-xs font-mono text-stone-700 truncate">{panel.code}</p>
                      <p className="text-xs text-stone-400 truncate">{[panel.collection, panel.size].filter(Boolean).join(' · ')}</p>
                      <div className="flex items-center justify-between gap-1 pt-1">
                        <Input
                          type="number"
                          min={1}
                          value={panel.quantity}
                          onChange={(e) => handleQuantityChange(clientData.client_id, panel.product_id, e.target.value)}
                          onBlur={handleQuantityBlur}
                          className="h-7 w-14 text-xs px-1.5"
                          data-testid={`panel-quantity-${clientData.client_id}-${panel.product_id}`}
                        />
                        <span className="text-xs font-semibold text-[#009392]">€{panel.line_total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-400 mb-4">Sem painéis selecionados para este cliente.</p>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-stone-100">
              <Button variant="outline" size="sm" onClick={() => openPanelDialog(clientData.client_id)} data-testid={`add-panels-${clientData.client_id}`}>
                <Plus size={14} /> Adicionar Painéis/Expositores
              </Button>
              <p className="font-semibold text-stone-800">
                Subtotal: <span className="text-[#009392]">€{clientData.subtotal.toFixed(2)}</span>
              </p>
            </div>
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={() => setShowClientDialog(true)} className="w-full border-dashed" data-testid="btn-add-client-proposal">
        <Plus size={16} /> Adicionar Cliente
      </Button>

      {/* Total geral */}
      <div className="gresart-card bg-[#009392] text-white flex items-center justify-between" data-testid="proposal-grand-total">
        <span className="font-semibold">Total Geral do Investimento</span>
        <span className="text-xl font-bold">€{proposal.grand_total.toFixed(2)}</span>
      </div>

      {/* Dialog: Adicionar Cliente */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="max-w-md" data-testid="dialog-add-client-proposal">
          <DialogHeader><DialogTitle>Adicionar Cliente à Proposta</DialogTitle></DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input autoFocus placeholder="Pesquisar cliente..." value={clientSearch} onChange={(e) => handleClientSearchChange(e.target.value)} className="pl-10" data-testid="input-add-client-search" />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 mt-2">
            {clientResults.map((c) => (
              <button key={c.id} onClick={() => handleAddClient(c)} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-left transition-colors border border-stone-100" data-testid={`add-client-option-${c.id}`}>
                <span className="text-sm text-stone-700 truncate">{c.name}</span>
                {c.city && <span className="text-xs text-stone-400 flex-shrink-0">{c.city}</span>}
              </button>
            ))}
            {clientSearch.trim().length >= 2 && clientResults.length === 0 && (
              <p className="text-sm text-stone-400 text-center py-4">Nenhum cliente encontrado</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Adicionar Painéis/Expositores do catálogo PPM */}
      <Dialog open={showPanelDialog} onOpenChange={setShowPanelDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col" data-testid="dialog-add-panels-proposal">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Adicionar Painéis/Expositores do Catálogo PPM</DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2 flex-shrink-0">
            {[{ label: "Todos", value: "" }, { label: "Painéis Técnicos", value: "PAINÉIS TÉCNICOS" }, { label: "Expositores", value: "EXPOSITORES" }, { label: "Pastas Técnicas", value: "PASTAS TÉCNICAS" }].map(tab => (
              <button
                key={tab.value || 'todos'}
                onClick={() => handlePanelTypeFilterChange(tab.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${panelTypeFilter === tab.value ? 'bg-[#009392] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                data-testid={`panel-type-filter-${tab.value || 'todos'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="search-input-container mb-4 flex-shrink-0">
            <MagnifyingGlass size={18} />
            <Input
              placeholder="Pesquisar por código, coleção..."
              value={panelSearch}
              onChange={handlePanelSearchChange}
              className="gresart-input pl-10"
              data-testid="search-panel-catalog"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 pr-2">
            {catalogProducts.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                {panelSearch ? "Nenhum produto encontrado" : "Não há produtos no catálogo"}
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {catalogProducts.map((product) => {
                  const isSelected = selectedProductIds.includes(product.id);
                  return (
                    <div
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${isSelected ? "border-[#009392] ring-2 ring-[#009392]/20" : "border-transparent hover:border-stone-300"}`}
                      data-testid={`panel-catalog-option-${product.id}`}
                    >
                      <div className="bg-stone-50 p-2">
                        <img src={product.image_url} alt={product.code} className="w-full h-auto max-h-32 object-contain mx-auto" />
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-[#009392] rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                      <div className="p-2 flex items-center justify-between gap-1">
                        <p className="text-xs font-mono text-stone-700 truncate">{product.code}</p>
                        {product.value != null && (
                          <p className="text-xs font-semibold text-[#009392] flex-shrink-0">€{parseFloat(product.value).toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-stone-100 flex-shrink-0 bg-white">
            <p className="text-sm text-stone-500">{selectedProductIds.length} painel(éis) selecionado(s)</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPanelDialog(false)}>Cancelar</Button>
              <Button onClick={handleConfirmAddPanels} disabled={selectedProductIds.length === 0} className="bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="btn-confirm-add-panels">
                Adicionar Selecionados
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
